import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FormService, FormField } from "@shared/services/FormServices";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FIELD_CLASSES } from "@shared/utils/constants";
import { PageHeader } from "@shared/components/PageHeader";
import { Button } from "@shared/ui/button";
import { useBillingFrequency, useCreatePlan, useGetPlanDetail, useUpdatePlan } from "../../../hooks";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { queryClient } from "@shared/api/queries/queryClient";
import { QueryKeyFactory } from "@shared/api/queries/queryKeyFactory";

interface BasicInfoProps {
  data?: any;
  onDataChange?: (data: any) => void;
  onNext?: () => void;
  onPrevious?: () => void;
  isFirstStep?: boolean;
  isLastStep?: boolean;
}



export const BasicInfo: React.FC<BasicInfoProps> = ({
  data,
  onDataChange,
  onNext,
  onPrevious,
  isFirstStep,
  isLastStep,
}) => {
  const { action, planId } = useParams();
  const [hasInitialData] = useState(() => Object.keys(data || {}).length > 0);
  const queryPlanId = hasInitialData ? undefined : planId;
  const remoteUtilityId = useRemoteUtilityId();
  const { data: planDetail } = useGetPlanDetail(queryPlanId);
  const { mutateAsync: createPlan, isPending: isCreating } = useCreatePlan();
  const { mutateAsync: updatePlan, isPending: isUpdating } = useUpdatePlan();
  const isPending = isCreating || isUpdating;

//useBillingFrequency
const {data:billingFrequencyData}=useBillingFrequency();
  const billingFrequencyOptions = useMemo(
    () =>
      billingFrequencyData?.result?.map((billing) => ({
        value: billing.key,
        label: billing.value,
      })) || [],
    [billingFrequencyData]
  );
  const basicInfoFields: FormField[] = useMemo(
    () => [
      {
        name: "planName",
        label: "Plan Name",
        type: "text",
        required: true,
        placeholder: "e.g. Residential Smart-Home Plan",
        classes: FIELD_CLASSES,
        helperText: "Enter the full name of the plan.",
      },
      {
        name: "planCode",
        label: "Plan Code",
        type: "text",
        required: true,
        placeholder: "RES-SMART",
        helperText: "Used in billing files and APIs. Format: CATEGORY-VARIANT.",
        classes: FIELD_CLASSES,
      },
      {
        name: "validateForm",
        label: "Effective From",
        type: "datepicker",
        required: true,
        helperText: "Date this plan becomes assignable to consumers.",
        classes: FIELD_CLASSES,
      },
      {
        name: "validatTo",
        label: "Effective To",
        type: "datepicker",
        helperText: "Optional. Leave blank for an open-ended plan.",
        classes: FIELD_CLASSES,
      },
      {
        name: "billingCycle",
        label: "Billing Cycle",
        type: "select",
        required: true,
        placeholder: "Select Billing Cycle",
        options: billingFrequencyOptions,
        helperText: "How often a bill is generated for consumers on this plan.",
        classes: FIELD_CLASSES,
      },
      {
        name: "description",
        label: "Description",
        type: "textarea",
        placeholder: "Internal description of this plan. Visible to operations and approvers.",
        helperText: "Brief description of the plan for internal reference and approvers.",
        classes: FIELD_CLASSES,
        className: "col-span-2",
      },
    ],
    [billingFrequencyOptions],
  );

  const form = FormService.createForm({
    fields: basicInfoFields,
    defaultValues: data || {},
    mode: "onChange",
  });

  const { handleSubmit } = form;
  const isLocalStorageRestored = React.useRef(false);

  useEffect(() => {
    if (hasInitialData && data && !isLocalStorageRestored.current) {
      FormService.populateForm(form, data, basicInfoFields);
      isLocalStorageRestored.current = true;
    }
  }, [hasInitialData, data, form, basicInfoFields]);

  const isPopulated = React.useRef(false);

  useEffect(() => {
    if (hasInitialData) return;
    if (isPopulated.current) return;
    if (planDetail?.result && planId && planId !== "create") {
      isPopulated.current = true;
      const result = planDetail.result;

      const valuesToSet = {
        planName: result.planName,
        planCode: result.planCode,
        validateForm: result.effectiveDate,
        validatTo: result.endDate,
        billingCycle: result.overview?.planConfiguration?.billingCycle,
        description: result.description,
      };

      FormService.populateForm(form, valuesToSet, basicInfoFields);
    }
  }, [hasInitialData, planDetail?.result, planId, form, basicInfoFields]);

  const onDataChangeRef = React.useRef(onDataChange);
  const dataRef = React.useRef(data);

  useEffect(() => {
    onDataChangeRef.current = onDataChange;
    dataRef.current = data;
  }, [onDataChange, data]);

  // Sync data to parent component — emit immediately on mount AND on every change
  useEffect(() => {
    if (!onDataChangeRef.current) return;
    onDataChangeRef.current({ ...dataRef.current, ...form.getValues() });
    const subscription = form.watch((value) => {
      onDataChangeRef.current?.({ ...dataRef.current, ...value });
    });
    return () => subscription.unsubscribe();
  }, [form]);

  const navigate = useNavigate();

  const savePlan = async (formData: any, onSuccess: () => void) => {
    try {
      const payload = {
        plan_name: formData.planName,
        short_name: formData.planCode,
        billing_frequency: formData.billingCycle,
        start_date: formData.validateForm,
        remote_utility_id: remoteUtilityId,
        ...(formData.description && { description: formData.description }),
        ...(formData.validatTo && { validity: formData.validatTo }),
        ...(!(planId && planId !== "create") && { status: 4 }),
        current_step: 1
      };
      
      let newPlanId = planId;

      if (planId && planId !== "create") {
         if (action !== 'edit') {
           await updatePlan({ planId, payload });
         }
      } else {
         const response = await createPlan(payload);
         newPlanId = response?.result?.id || response?.id;
      }
      
      if (onDataChange) {
        onDataChange({ ...formData, planId: newPlanId });
      }

      if (newPlanId && (!planId || planId === "create")) {
        queryClient.refetchQueries({ queryKey: QueryKeyFactory.module.onboarding.planTarrif.planTable().slice(0, 3)});
        navigate(`/plan-tarrif/plan/create/${newPlanId}${window.location.hash}`, { replace: true });
      }

      onSuccess();
    } catch (error) {
      console.error("Failed to save plan:", error);
    }
  };

  const onSubmitForm = (formData: any) => savePlan(formData, () => onNext && onNext());
  const onPreviousSubmit = (formData: any) => savePlan(formData, () => onPrevious && onPrevious());

  return (
    <div className="">
      <div className="mb-6">
        <PageHeader
          title="Basic Information"
          subtitle="Provide identifying details. The plan code must be unique within your utility."
        />
      </div>
      <DynamicForm
        fields={basicInfoFields}
        form={form}
        onSubmit={onSubmitForm}
        gridColumns={2}
        config={{
          showSubmitButton: false,
          showResetButton: false,
        }}
      />

      <div className="flex items-center justify-between mt-6 border-t pt-6">
        <div>
          {!isFirstStep && onPrevious && (
            <Button type="button" variant="outline" onClick={handleSubmit(onPreviousSubmit)} disabled={isPending}>
              Previous
            </Button>
          )}
        </div>
        <Button type="button" onClick={handleSubmit(onSubmitForm)} disabled={isPending}>
          {isPending ? "Saving..." : (isLastStep ? "Finish" : "Next")}
        </Button>
      </div>
    </div>
  );
};

export default BasicInfo;
